import { create } from "zustand";
import { z } from "zod";
import hashObject from "object-hash";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type { SetStateAction, Dispatch, ChangeEvent } from "react";
import Decimal from "decimal.js";
import { useStytchMember } from "@stytch/react/b2b";
import {
  ColumnDef,
  Table as ReactTable,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Delete as DeleteIcon,
  StepForward as StepForwardIcon,
  Dot as DotIcon,
  RotateCcw as RotateCcwIcon,
  DownloadCloud,
  Trash2,
  Plus,
  MoreHorizontal,
  Check as CheckIcon,
  Link as LinkIcon,
  Archive as ArchiveXIcon,
  ListFilter as ListFilterIcon,
  Filter as FilterIcon,
  Eye as EyeIcon,
  SquarePen as SquarePenIcon,
  CircleHelp as CircleHelpIcon,
  CopyIcon,
} from "lucide-react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { GenerateLoadingOrError, CommonDestinationTimezone } from "../../lib/utils";
import {
  useDownloadMarginReportData,
  useArchivePosition,
  useEditManualPosition,
  formulateOptionMonths,
  OptionMonthOptions,
  generateAccountLabel,
  generateCurrentAndFutureMonthOptions,
  DownloadRecentContractFilterValidation,
  DownloadFilterValidation,
  useDownloadOpenData,
  useCreatePosition,
  PutOrCallOptions,
  CreatePositionValidation,
  ContractListOptionParams,
  useListOpenPositions,
  useDownloadOpenPositions,
  useDownloadOpenContracts,
  useListAccounts,
  TradingAccountOpenPosition,
  TradingAccount,
  TradingContract,
  ContractType,
  ContractTypeOptions,
  CommodityOptions,
  useListOpenContracts,
  useListOpenContractFilters,
  useArchiveContract,
  useUnPairContract,
  usePairContract,
  createContractsExcelBuffer,
  contractNumberKeys,
  posNumberKeys,
  findMatchOption,
  PositionContractPair,
  useListRecentArchivedPositions,
  TradingAccountOpenPositionAudit,
  useMarkContractManualFullyPaired,
  useMarkPositionManualFullyPaired,
  useBulkMarkPositionsManualFullyPaired,
  useMarkPositionWillExpire,
  useDownloadOpenRecentContractData,
  useDownloadSummaryData,
  useListClosedContracts,
  useUnArchiveContract,
  useListPositionAdjustments,
  useDeletePositionAdjustment,
  useCreatePositionAdjustment,
  TradingAccountOpenPositionAdjustment,
  RecentReportQueryValidation,
  useGenerateInvoiceFeesReport,
  useGenerateFeeReport,
  OtcReportDateRangeValidation,
} from "../../lib/api/otc";
import { getOptionPredictedExpirationDate, getQuarterlyOptionMonthExpirationDate } from "@/lib/api/common";
import { useListTraders } from "../../lib/api/trader";
import { Trader } from "../../lib/api/reports";
import {
  formatDateWithoutTimezone,
  formatDateWithoutTimezoneToDate
} from "../../lib/api/date";
import { useListCustomers } from "../../lib/api/customer";
import { Customer } from "../../lib/api/reports";
import {
  TicketRunSummary,
  TicketsSummary,
  ProviderTicketCount,
  Provider,
} from "../../lib/models/index";
import { ProviderType } from "../../lib/models/provider";
import {
  TicketProviderSummary,
  TicketProviderRecentRunPartial,
  TicketProviderBatchDownloadPartial,
} from "../../lib/models/tickets";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { MultiSelect } from "../../components/ui/selectMulti";
import { SelectInput } from "../../components/ui/SelectInput";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { useToast } from "../../components/ui/use-toast";
import {
  DataTable,
  DataTableProps,
} from "../../components/ui/virtualizedTable";
import { DataTableColumn, DataTable as NewDataTable } from "@/components/ui/DataTable/index";
import { downloadCsvTextAsFile } from "../../lib/browser/download";
import { Loader } from "../../components/ui/loader";
import { DatePicker } from "../../components/ui/typeable_date_picker";
import { handleFocus } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { ValidationError, PossibleReturnType } from "../../lib/responseResults";
import { Combobox } from "../../components/ui/combobox";
import { cn, createDateLabel } from "../../lib/utils";
import { downloadBinaryData } from "../../lib/browser/download";
import { SelectOptionItem } from "../../lib/models/ui";
import { PossiblePackage, doesUserHavePackages } from "../../lib/models/auth";
import useDebounce from "./useDebounce";
import { formatNumber } from "./common";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../components/ui/hover-card"
import { Separator, SeparatorWithText } from "@/components/ui/separator";
import {
  createAccountListOptions, transformGenericApiData,
  useBearStore, ContractForm,
  BearSelectMultiInput, BearSelectInput, BearDatePicker, BearFloatInput, BearIntInput, BearInput,
} from "./ContractForm";
import { OtcContractFormModal } from "../contracts/shared/OtcContractFormModal";
import { OtcContractDetailModal } from "@/section/otc/contract-detail-view";
import {
  ignoreSafeParseDecimal, BearState, PairingData,
} from "./OtcBearState";
import { StagedOtcContracts } from "./stagedContracts";
import { OtcAmendmentReviewSection } from "./AmendmentReviewSection";
import { ErrorCorrectionsSection } from "./ErrorCorrectionsSection";
import { OtcInvoicesSection } from "./invoices/OtcInvoicesSection";
import { ReportsSection } from "./reports";
import { formatContractReferenceDisplay } from "./contract-form-v2/contract-reference-utils";
import { IndeterminateCheckbox } from "../cfr/components/IndeterminateCheckbox";

const baseOpenPositionColumns: any[] = [
  {
    id: "account",
    header: "Account",
    cell: ({ row }: any) => (
      <span className="">{generateAccountLabel(row.original.TradingAccount.account_id) || row.original.TradingAccount.account_id}</span>
    ),
    enableSorting: true,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "base_customer_name",
    header: "Customer",
    cell: ({ row }: any) => row.original.customer?.long_name || "",
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: "date",
    header: "Date",
    cell: ({ row }: any) =>
      moment
        .tz(row.original.date, CommonDestinationTimezone)
        .format("MM/DD/YYYY"),
    enableSorting: false,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: "long",
    header: "Long",
    cell: ({ row }: any) => row.original.long_qty,
    enableSorting: false,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: "short",
    header: "Short",
    cell: ({ row }: any) => row.original.short_qty,
    enableSorting: false,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: "put_call",
    header: "Put/Call",
    cell: ({ row }: any) => row.original.put_or_call,
    enableSorting: false,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "commodity",
    header: "Commodity",
    cell: ({ row }: any) => row.original.commodity,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "optionMonth",
    header: "Reference Month",
    cell: ({ row }: any) => row.original.option_month || "",
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "strike_price",
    header: "Strike Price",
    cell: ({ row }: any) => row.original.strike_price,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "price",
    header: "Price",
    cell: ({ row }: any) => row.original.price,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "premium",
    header: "Option Premium",
    cell: ({ row }: any) => row.original.premium || 0,
    onCellClick: true,
    enableColumnFilter: false,
  },
  {
    id: "bushels",
    header: "Bushels",
    cell: ({ row }: any) => formatNumber(row.original.bushel),
    onCellClick: true,
    enableColumnFilter: false,
  },
];

const newBaseOpenPositionColumns: DataTableColumn<any>[] = [
  {
    id: "contract_no",
    accessorKey: "contract_no",
    accessorFn: (row: any) => row.contract_no ?? "",
    header: "Contract #",
  },
  {
    id: "account",
    accessorKey: "account",
    accessorFn: (row: any) => generateAccountLabel(row.TradingAccount?.account_id) || row.TradingAccount?.account_id || "",
    header: "Account",
  },
  {
    id: "base_customer_name",
    accessorKey: "base_customer_name",
    accessorFn: (row: any) => row.customer?.long_name ?? "",
    header: "Customer",
  },
  {
    id: "date",
    accessorKey: "date",
    accessorFn: (row: any) => row.date,
    columnFilterType: 'date' as const,
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {moment.tz(row.original.date, CommonDestinationTimezone).format("MM/DD/YYYY")}
      </span>
    ),
  },
  {
    id: "long_qty",
    accessorKey: "long_qty",
    accessorFn: (row: any) => row.long_qty,
    header: "Long",
  },
  {
    id: "short_qty",
    accessorKey: "short_qty",
    accessorFn: (row: any) => row.short_qty,
    header: "Short",
  },
  {
    id: "put_or_call",
    accessorKey: "put_or_call",
    accessorFn: (row: any) => row.put_or_call ?? "",
    header: "Put/Call",
  },
  {
    id: "commodity",
    accessorKey: "commodity",
    accessorFn: (row: any) => row.commodity ?? "",
    header: "Commodity",
  },
  {
    id: "option_month",
    accessorKey: "option_month",
    accessorFn: (row: any) => row.option_month ?? "",
    header: "Reference Month",
  },
  {
    id: "strike_price",
    accessorKey: "strike_price",
    accessorFn: (row: any) => row.strike_price,
    header: "Strike Price",
  },
  {
    id: "price",
    accessorKey: "price",
    accessorFn: (row: any) => row.price,
    header: "Price",
  },
  {
    id: "premium",
    accessorKey: "premium",
    accessorFn: (row: any) => row.premium,
    header: "Option Premium",
  },
  {
    id: "bushel",
    accessorKey: "bushel",
    accessorFn: (row: any) => row.bushel,
    header: "Bushels",
    cell: ({ row }: any) => formatNumber(row.original.bushel),
  },
  {
    id: "is_manually_inserted",
    accessorKey: "is_manually_inserted",
    accessorFn: (row) => row.is_manually_inserted ? "Yes" : "No",
    header: "Manually Inserted",
  },
];

const posPairOpenPositionColumns: any[] = [
  {
    id: "pair",
    header: "Pair",
    cell: ({ row }: any) => (
      <Button className="" variant="outline">
        Pair
      </Button>
    ),
    enableSorting: true,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  ...baseOpenPositionColumns,
];

const posUnpairOpenPositionColumns: any[] = [
  {
    id: "unpair",
    header: "Pair",
    cell: ({ row }: any) => (
      <Button className="w-24" variant="outline">
        Un-Pair
      </Button>
    ),
    enableSorting: true,
    enableHiding: false,
    onCellClick: true,
    enableColumnFilter: false,
  },
  ...baseOpenPositionColumns,
];

export const nonActionContractColumns: any[] = [
  // Date columns - proper date handling for sorting, filtering, and rendering
  {
    id: "date",
    accessorKey: "date",
    accessorFn: (row: any) => row.date,
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {row.original.date_label}
      </span>
    ),
  },
  // Relation columns - use accessorFn for proper filter display
  {
    id: "CreatedByUser.name",
    accessorKey: "CreatedByUser.name",
    accessorFn: (row: any) => row.CreatedByUser?.name ?? "",
    header: "Created By",
    cell: ({ row }: any) => row.original.CreatedByUser?.name ?? "",
  },
  {
    id: "UpdatedByUser.name",
    accessorKey: "UpdatedByUser.name",
    accessorFn: (row: any) => row.UpdatedByUser?.name ?? "",
    header: "Updated By",
    cell: ({ row }: any) => row.original.UpdatedByUser?.name ?? "",
  },
  {
    id: "account_no_a",
    accessorKey: "account_no_a",
    accessorFn: (row: any) => row.options_account?.no ?? "",
    header: "OTC Brokerage",
    cell: ({ row }: any) => row.original.options_account?.no || "",
  },
  {
    id: "account_no_b",
    accessorKey: "account_no_b",
    accessorFn: (row: any) => row.futures_account?.no ?? "",
    header: "HTA Brokerage",
    cell: ({ row }: any) => row.original.futures_account?.no || "",
  },
  {
    id: "contract_no",
    accessorKey: "contract_no",
    accessorFn: (row: any) => row.contract_no ?? "",
    header: "Contract #",
    cell: ({ row }: any) => row.original.contract_no,
  },
  {
    accessorKey: "contract_reference",
    header: "Contract Reference",
    cell: ({ row }: any) => formatContractReferenceDisplay(row.original.contract_reference),
    enableSorting: true,
    enableHiding: false,
    enableColumnFilter: true,
  },
  {
    id: "customer_id",
    accessorKey: "customer_id",
    accessorFn: (row: any) => row.customer?.long_name ?? "",
    header: "Customer",
  },
  {
    id: "trader_id",
    accessorKey: "trader_id",
    accessorFn: (row: any) => row.Trader?.name ?? "",
    header: "Trader",
    cell: ({ row }: any) => row.original.Trader?.name || "",
  },
  {
    id: "contract_type",
    accessorKey: "contract_type",
    accessorFn: (row: any) => row.contract?.type ?? "",
    header: "Contract Type",
    cell: ({ row }: any) => (
      <span className="">{row.original.contract?.type}</span>
    ),
  },
  // Numeric columns with accessorFn for proper sorting
  {
    id: "quantity",
    accessorKey: "quantity",
    accessorFn: (row: any) => row.quantity,
    header: "Quantity",
    cell: ({ row }: any) => formatNumber(row.original.quantity),
  },
  {
    id: "commodity_label",
    accessorKey: "commodity_label",
    accessorFn: (row: any) => row.commodity_label ?? "",
    header: "Commodity",
    cell: ({ row }: any) => row.original.commodity_label,
  },
  {
    id: "crop_year",
    accessorKey: "crop_year",
    accessorFn: (row: any) => row.crop_year ?? "",
    header: "Crop Year",
    cell: ({ row }: any) => row.original.crop_year,
  },
  {
    id: "option_month",
    accessorKey: "option_month",
    accessorFn: (row: any) => row.option_month ?? "",
    header: "OTC Opt. Month",
    cell: ({ row }: any) => row.original.option_month,
  },
  {
    id: "option_month_etc_one",
    accessorKey: "option_month_etc_one",
    accessorFn: (row: any) => row.option_month_etc_one ?? "",
    header: "HTA DU Opt. Month",
    cell: ({ row }: any) => row.original.option_month_etc_one || "",
  },
  {
    id: "notes",
    accessorKey: "notes",
    accessorFn: (row: any) => row.notes ?? "",
    header: "Notes",
    cell: ({ row }: any) => row.original.notes || "",
  },
  // Date columns
  {
    id: "start_date",
    accessorKey: "start_date",
    accessorFn: (row: any) => row.start_date,
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Start Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {row.original.start_date_label}
      </span>
    ),
  },
  {
    id: "end_date",
    accessorKey: "end_date",
    accessorFn: (row: any) => row.end_date,
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Expiration Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {row.original.end_date_label}
      </span>
    ),
  },
  {
    id: "shipment_start_date",
    accessorKey: "shipment_start_date",
    accessorFn: (row: any) => row.shipment_start_date,
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Shipment Start Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {row.original.shipment_start_date_label}
      </span>
    ),
  },
  {
    id: "shipment_end_date",
    accessorKey: "shipment_end_date",
    accessorFn: (row: any) => row.shipment_end_date,
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Shipment End Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {row.original.shipment_end_date_label}
      </span>
    ),
  },
  {
    id: "weeks",
    accessorKey: "weeks",
    accessorFn: (row: any) => row.weeks,
    header: "Weeks",
    cell: ({ row }: any) => row.original.weeks,
  },
  {
    id: "period_qty",
    accessorKey: "period_qty",
    accessorFn: (row: any) => row.period_qty,
    header: "Period Qty",
    cell: ({ row }: any) => row.original.period_qty,
  },
  {
    id: "knockout",
    accessorKey: "knockout",
    accessorFn: (row: any) => row.knockout ?? "",
    header: "Knockout",
    cell: ({ row }: any) => row.original.knockout,
  },
  {
    id: "step_one",
    accessorKey: "step_one",
    accessorFn: (row: any) => row.step_one ?? "",
    header: "Step 1",
    cell: ({ row }: any) => row.original.step_one,
  },
  {
    id: "step_two",
    accessorKey: "step_two",
    accessorFn: (row: any) => row.step_two ?? "",
    header: "Step 2",
    cell: ({ row }: any) => row.original.step_two,
  },
  {
    id: "step_three",
    accessorKey: "step_three",
    accessorFn: (row: any) => row.step_three ?? "",
    header: "Step 3",
    cell: ({ row }: any) => row.original.step_three,
  },
  {
    id: "double_up",
    accessorKey: "double_up",
    accessorFn: (row: any) => row.double_up ?? "",
    header: "Double Up",
    cell: ({ row }: any) => row.original.double_up,
  },
  {
    id: "option_premium",
    accessorKey: "option_premium",
    accessorFn: (row: any) => row.option_premium,
    header: "Option Premium",
    cell: ({ row }: any) => row.original.option_premium,
  },
  {
    id: "contract_fee",
    accessorKey: "contract_fee",
    accessorFn: (row: any) => row.contract_fee,
    header: "Initial Premium + Fee",
    cell: ({ row }: any) => row.original.contract_fee,
  },
  {
    id: "margin",
    accessorKey: "margin",
    accessorFn: (row: any) => row.margin,
    header: "Lighthouse Fee",
    cell: ({ row }: any) => formatNumber(row.original.margin),
  },
  {
    id: "margin_amount",
    accessorKey: "margin_amount",
    accessorFn: (row: any) => row.margin_amount,
    header: "Margin Amount",
    cell: ({ row }: any) => formatNumber(row.original.margin_amount),
  },
  {
    id: "trade_reference",
    accessorKey: "trade_reference",
    accessorFn: (row: any) => row.trade_reference ?? "",
    header: "Trade Reference",
    cell: ({ row }: any) => row.original.trade_reference,
  },
  {
    id: "futures_price",
    accessorKey: "futures_price",
    accessorFn: (row: any) => row.futures_price,
    header: "Futures Price",
    cell: ({ row }: any) => formatNumber(row.original.futures_price),
  },
  {
    id: "otc_futures",
    accessorKey: "otc_futures",
    accessorFn: (row: any) => row.otc_futures,
    header: "OTC Futures",
    cell: ({ row }: any) => formatNumber(row.original.otc_futures),
  },
  {
    id: "rolled_option_month",
    accessorKey: "rolled_option_month",
    accessorFn: (row: any) => row.rolled_option_month ?? "",
    header: "Futures Rolled Month",
    cell: ({ row }: any) => row.original.rolled_option_month,
  },
  {
    id: "rolled_basis_month",
    accessorKey: "rolled_basis_month",
    accessorFn: (row: any) => row.rolled_basis_month ?? "",
    header: "Basis Rolled Month",
    cell: ({ row }: any) => row.original.rolled_basis_month,
  },
  {
    id: "carry",
    accessorKey: "carry",
    accessorFn: (row: any) => row.carry,
    header: "Futures Carry",
    cell: ({ row }: any) => ignoreSafeParseDecimal(row.original.carry)?.toFixed(2),
  },
  {
    id: "basis_carry",
    accessorKey: "basis_carry",
    accessorFn: (row: any) => row.basis_carry,
    header: "Basis Carry",
    cell: ({ row }: any) => ignoreSafeParseDecimal(row.original.basis_carry)?.toFixed(2),
  },
  {
    id: "adj_option_premium",
    accessorKey: "adj_option_premium",
    accessorFn: (row: any) => row.adj_option_premium,
    header: "Adj Option Premium",
    cell: ({ row }: any) => row.original.adj_option_premium,
  },
  {
    id: "adj_contract_fee",
    accessorKey: "adj_contract_fee",
    accessorFn: (row: any) => row.adj_contract_fee,
    header: "Adj Initial Premium + Fee",
    cell: ({ row }: any) => row.original.adj_contract_fee,
  },
  {
    id: "adj_margin",
    accessorKey: "adj_margin",
    accessorFn: (row: any) => row.adj_margin,
    header: "Adj Lighthouse Fee",
    cell: ({ row }: any) => formatNumber(row.original.adj_margin),
  },
  {
    id: "adj_margin_amount",
    accessorKey: "adj_margin_amount",
    accessorFn: (row: any) => row.adj_margin_amount,
    header: "Adj Margin Amount",
    cell: ({ row }: any) => formatNumber(row.original.adj_margin_amount),
  },
  {
    id: "last_adjusted_date",
    accessorKey: "last_adjusted_date",
    accessorFn: (row: any) => row.last_adjusted_date,
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v: any) => moment.tz(v as string, CommonDestinationTimezone),
    header: "Last Adj Date",
    cell: ({ row }: any) => (
      <span className="text-gray-600 text-sm">
        {row.original.last_adjusted_date_label}
      </span>
    ),
  },
];

const posPairOpenContractColumns: any[] = [
  {
    id: "pair",
    header: "Pair",
    cell: ({ row }: any) => (
      <Button className="" variant="outline">
        Pair
      </Button>
    ),
    size: 80,
    enableSorting: true,
    enableHiding: false,
    enableColumnFilter: false,
    onCellClick: true,
    disableRowClick: true,
  },
  ...nonActionContractColumns,
];

const posUnpairOpenContractColumns: any[] = [
  {
    id: "unpair",
    header: "Pair",
    cell: ({ row }: any) => (
      <Button className="w-24" variant="outline">
        Un-Pair
      </Button>
    ),
    size: 80,
    enableSorting: true,
    enableHiding: false,
    enableColumnFilter: false,
    onCellClick: true,
    disableRowClick: true,
  },
  ...nonActionContractColumns,
];

function should_refresh_data({
  current_state,
  new_state,
}: {
  current_state?: any;
  new_state?: any[];
}) {
  if (!current_state && !!new_state) {
    return true;
  }
  if (!!current_state && !new_state) {
    return false;
  }

  if (!current_state && !new_state) {
    return false;
  }

  return current_state! !== hashObject(new_state!);
}

export function OtcDashboard() {
  const position_filters = useBearStore(
    (state) => state.position_filters.filters,
  );
  const contract_filters = useBearStore(
    (state) => state.contract_filters.filters,
  );
  const listTradersRes = useListTraders();
  const openPositionsRes = useListOpenPositions({ filters: position_filters, listOtcAccountsOnly: true });
  const openContractsRes = useListOpenContracts({ filters: contract_filters });
  const accountsRes = useListAccounts(false);
  const customersResult = useListCustomers();
  const recentArchivedPositionsRes = useListRecentArchivedPositions();

  const isMissingData = (!openPositionsRes?.data?.data) || (!openContractsRes?.data?.data);

  if (
    [listTradersRes, recentArchivedPositionsRes, customersResult, accountsRes].some((result) => result.isLoading) || isMissingData
  ) {
    return (
      <div className="flex flex-col space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    );
  }

  if (
    [listTradersRes, recentArchivedPositionsRes, customersResult, accountsRes].some(
      (result) => result.error || !result?.data?.success,
    )
  ) {
    return (
      <div className="flex flex-col space-y-2">
        Failed to communicate with servers. Please refresh and try again
      </div>
    );
  }

  const openPositionsData = openPositionsRes?.data?.data || {};
  const openContractsData = openContractsRes?.data?.data || {};

  return (
    <DashboardView
      isRefreshingData={openPositionsRes.isLoading || openContractsRes.isLoading || false}
      traders={listTradersRes?.data?.data || []}
      customers={customersResult?.data?.data || []}
      recentArchivedPositions={recentArchivedPositionsRes?.data?.data || []}
      openContractsPaired={openContractsData.pairedContracts || []}
      openContractsUnpaired={openContractsData.unpairedContracts || []}
      openPositionsPaired={(openPositionsData.pairedPositions || []).filter((pos: TradingAccountOpenPosition) => !["AA053"].includes(pos?.TradingAccount?.account_id || ""))}
      openPositionsUnpaired={(openPositionsData.unpairedPositions || []).filter((pos: TradingAccountOpenPosition) => !["AA053"].includes(pos?.TradingAccount?.account_id || ""))}
      refetchPositions={openPositionsRes.fetchData}
      refetchContracts={openContractsRes.fetchData}
      pairs={openPositionsData.pairs || []}
      accounts={accountsRes?.data?.data || []}
    />
  );
}

export function DashboardView({
  isRefreshingData,
  recentArchivedPositions,
  openContractsPaired,
  openContractsUnpaired,
  openPositionsPaired,
  openPositionsUnpaired,
  pairs,
  accounts,
  customers,
  traders,
  refetchPositions,
  refetchContracts,
}: {
  isRefreshingData: boolean;
  recentArchivedPositions: TradingAccountOpenPositionAudit[];
  openContractsPaired: TradingContract[];
  openContractsUnpaired: TradingContract[];
  openPositionsPaired: TradingAccountOpenPosition[];
  openPositionsUnpaired: TradingAccountOpenPosition[];
  pairs: PositionContractPair[];
  accounts: TradingAccount[];
  customers: Customer[];
  traders: Trader[];
  refetchPositions: any;
  refetchContracts: any;
}) {

  return (
    <>
      <div className="flex flex-col mb-2">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          OTC Dashboard
        </h1>
      </div>
      <div className="flex items-start flex-col h-full">
        <Tabs defaultValue="open_positions" className="w-full">
          <TabsList>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="review">Review</TabsTrigger>
            <TabsTrigger value="amendment_review">Amendment Review</TabsTrigger>
            <TabsTrigger value="error_corrections">Error Corrections</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="open_positions">Open Positions</TabsTrigger>
            <TabsTrigger value="open_contracts">Open Contracts</TabsTrigger>
            <TabsTrigger value="recent_archived_positions">Recently Closed Positions</TabsTrigger>
            <TabsTrigger value="closed_contracts">Closed Contracts</TabsTrigger>
            <TabsTrigger value="position_adjustments">Position Adjustments</TabsTrigger>
          </TabsList>
          <TabsContent value="reports">
            <ReportsSection />
          </TabsContent>
          <TabsContent value="review">
            <StagedOtcContracts />
          </TabsContent>
          <TabsContent value="amendment_review">
            <OtcAmendmentReviewSection />
          </TabsContent>
          <TabsContent value="error_corrections">
            <ErrorCorrectionsSection />
          </TabsContent>
          <TabsContent value="invoices">
            <OtcInvoicesSection />
          </TabsContent>
          <TabsContent value="open_positions">
            <OpenPositionsSection
              traders={traders}
              accounts={accounts}
              customers={customers}
              openContractsUnpaired={openContractsUnpaired}
              openContractsPaired={openContractsPaired}
              openPositionsPaired={openPositionsPaired}
              openPositionsUnpaired={openPositionsUnpaired}
              refetchPositions={refetchPositions}
              refetchContracts={refetchContracts}
              pairs={pairs}
            />
          </TabsContent>
          <TabsContent value="open_contracts">
            <OpenContractsSection
              isRefreshingData={isRefreshingData}
              traders={traders}
              accounts={accounts}
              customers={customers}
              openContractsUnpaired={openContractsUnpaired}
              openContractsPaired={openContractsPaired}
              refetchContracts={refetchContracts}
              refetchPositions={refetchPositions}
              openPositionsPaired={openPositionsPaired}
              openPositionsUnpaired={openPositionsUnpaired}
              pairs={pairs}
            />
          </TabsContent>
          <TabsContent value="recent_archived_positions">
            <RecentArchivedPositionsSection recentArchivedPositions={recentArchivedPositions} />
          </TabsContent>
          <TabsContent value="closed_contracts">
            <ClosedContractsSection
              traders={traders}
              accounts={accounts}
              customers={customers}
            />
          </TabsContent>
          <TabsContent value="position_adjustments">
            <PositionAdjustmentsSection
              refetchPositions={refetchPositions}
              refetchContracts={refetchContracts}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function RecentArchivedPositionsSection({
  recentArchivedPositions,
}: {
  recentArchivedPositions: TradingAccountOpenPositionAudit[];
}) {
  return (
    <DataTable
      height="550px"
      columns={baseOpenPositionColumns}
      data={recentArchivedPositions}
      formId="RecentArchivedPos"
    />
  )
}

function OpenPositionsSection({
  openPositionsPaired,
  openPositionsUnpaired,
  openContractsPaired,
  openContractsUnpaired,
  pairs,
  traders,
  accounts,
  customers,
  refetchPositions,
  refetchContracts,
}: {
  openContractsPaired: TradingContract[];
  openContractsUnpaired: TradingContract[];
  openPositionsPaired: TradingAccountOpenPosition[];
  openPositionsUnpaired: TradingAccountOpenPosition[];
  pairs: PositionContractPair[];
  traders: Trader[];
  accounts: TradingAccount[];
  customers: Customer[];
  refetchPositions: any;
  refetchContracts: any;
}) {
  const set_pair_position = useBearStore((state) => state.set_pair_position);
  const pairing_data = useBearStore((state) => state.pairing_data);
  const set_edit_manual_position = useBearStore((state) => state.set_edit_manual_position);
  const [filterType, setFilterType] = useState<"all" | "paired" | "unpaired">("all");
  const [adjustmentPositions, setAdjustmentPositions] = useState<TradingAccountOpenPosition[]>([]);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [isBulkMarkingPaired, setIsBulkMarkingPaired] = useState(false);
  const adjustmentTableRef = useRef<ReactTable<TradingAccountOpenPosition> | null>(null);
  const bulkMarkPositionsFullyPaired = useBulkMarkPositionsManualFullyPaired();
  const { toast } = useToast();

  const pairedPositionIds = useMemo(() => {
    return new Set(openPositionsPaired.map((p) => p.id));
  }, [openPositionsPaired]);

  const filteredPositions = useMemo(() => {
    switch (filterType) {
      case "paired":
        return openPositionsPaired;
      case "unpaired":
        return openPositionsUnpaired;
      case "all":
      default:
        return [...openPositionsPaired, ...openPositionsUnpaired];
    }
  }, [filterType, openPositionsPaired, openPositionsUnpaired]);

  const positionRowBgColor = useCallback((row: any) => {
    if (pairedPositionIds.has(row.id)) {
      return { preset: 'light-blue' as const };
    }
    return { preset: 'light-yellow' as const };
  }, [pairedPositionIds]);

  const handleRowClick = (row: any, _columnId?: string) => {
    if (row.is_position_adjustment) return;
    set_pair_position({ position: row });
  };

  const allOpenPositionColumns: any[] = [
    {
      id: "selection-box",
      enableFiltering: false,
      enableSorting: false,
      enableGrouping: false,
      disableRowClick: true,
      header: ({ table }: { table: ReactTable<TradingAccountOpenPosition> }) => (
        <IndeterminateCheckbox
          className="mt-2"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }: { row: { original: TradingAccountOpenPosition; getIsSelected: () => boolean; getIsSomeSelected: () => boolean; getToggleSelectedHandler: () => () => void } }) => (
        <IndeterminateCheckbox
          checked={row.getIsSelected()}
          disabled={!!row.original.is_position_adjustment}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 50,
    },
    {
      id: "will_expire",
      accessorKey: "will_expire",
      accessorFn: (row: TradingAccountOpenPosition) => row.will_expire,
      header: "Will Expire",
      cell: ({ row }: { row: { original: TradingAccountOpenPosition } }) => (
        <MarkPositionWillExpireCheckbox
          position={row.original}
          refetchPositions={refetchPositions}
        />
      ),
      enableSorting: true,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
      size: 100,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const isPaired = pairedPositionIds.has(row.original.id);
        if (row.original.is_position_adjustment) {
          return <Badge variant="secondary">Temporary adjustment</Badge>;
        }
        return (
          <div className="flex items-center gap-2">
            {isPaired && row.original.is_manually_fully_paired ? (
              <MarkPositionFullyPairedContractButton
                id={row.original.id}
                refetchPositions={refetchPositions}
                message="Marked Un-Paired"
                buttonLabel="Un-Pair"
                buttonClassName="w-28"
                markStatus={false}
              />
            ) : isPaired ? (
              <Button className="w-28" variant="outline" disabled>Auto-Paired</Button>
            ) : (
              <MarkPositionFullyPairedContractButton
                id={row.original.id}
                refetchPositions={refetchPositions}
                message="Marked Paired"
                buttonLabel="Mark Paired"
                markStatus={true}
              />
            )}
            {row.original.is_manually_inserted ? (
              <Button
                variant="ghost"
                type="button"
                onClick={() => set_edit_manual_position(row.original)}
              >
                <SquarePenIcon />
              </Button>
            ) : null}
            {row.original.is_manually_inserted ? (
              <ArchivePositionButton id={row.original.id} refetchPositions={refetchPositions} />
            ) : null}
          </div>
        );
      },
      minSize: 320,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    },
    ...newBaseOpenPositionColumns,
  ];

  const customToolbarButtons = [
    ({ table }: { table: ReactTable<TradingAccountOpenPosition> }) => {
      const selectedPositions = table.getFilteredSelectedRowModel().rows
        .map((row: { original: TradingAccountOpenPosition }) => row.original)
        .filter((position: TradingAccountOpenPosition) => !position.is_position_adjustment);

      const markSelectedPaired = async (): Promise<void> => {
        if (isBulkMarkingPaired || selectedPositions.length === 0) return;
        setIsBulkMarkingPaired(true);

        try {
          const result = await bulkMarkPositionsFullyPaired({
            position_ids: selectedPositions.map((position: TradingAccountOpenPosition) => position.id),
          });
          if (!result.success) {
            toast({
              title: "Failed to mark selected positions paired",
              description: result.message || "",
              variant: "destructive",
            });
            return;
          }

          table.resetRowSelection();
          refetchPositions();
          toast({
            title: `Marked ${selectedPositions.length} position${selectedPositions.length === 1 ? "" : "s"} paired`,
            description: "",
          });
        } catch (error: unknown) {
          const message = (error as any)?.message;
          toast({
            title: "Failed to mark selected positions paired",
            description: message || "An unexpected error occurred",
            variant: "destructive",
          });
        } finally {
          setIsBulkMarkingPaired(false);
        }
      };

      return (
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={selectedPositions.length < 2}
          onClick={() => {
            adjustmentTableRef.current = table;
            setAdjustmentPositions(selectedPositions);
            setIsAdjustmentModalOpen(true);
          }}
        >
          Offset Selected ({selectedPositions.length})
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={selectedPositions.length === 0 || isBulkMarkingPaired}
          onClick={markSelectedPaired}
        >
          {isBulkMarkingPaired ? "Marking Paired..." : `Mark Selected Paired (${selectedPositions.length})`}
        </Button>
        <Button
          variant={filterType === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("all")}
        >
          All
        </Button>
        <Button
          variant={filterType === "paired" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("paired")}
        >
          Paired
        </Button>
        <Button
          variant={filterType === "unpaired" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("unpaired")}
        >
          UnPaired
        </Button>
      </div>
      );
    },
  ];

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-start w-full items-center">
          <ManualPositionFormModal
            refetchPositions={refetchPositions}
            customers={customers}
            accounts={accounts}
          />
          <OpenPositions
            traders={traders}
            refetchPositions={refetchPositions}
            accounts={accounts}
            areEmptyPositions={
              !openPositionsPaired.length && !openPositionsUnpaired.length
            }
            customers={customers}
          />
          <PairFormModal
            openContracts={[...openContractsPaired, ...openContractsUnpaired]}
            customers={customers}
            accounts={accounts}
            pairingData={pairing_data}
            setPairData={set_pair_position}
            pairs={pairs}
            refetchPositions={refetchPositions}
            refetchContracts={refetchContracts}
            onCloseModal={() => set_pair_position({ position: undefined as any })}
          />
        </div>
        <CreatePositionAdjustmentModal
          positions={adjustmentPositions}
          isOpen={isAdjustmentModalOpen}
          onClose={() => {
            setIsAdjustmentModalOpen(false);
            setAdjustmentPositions([]);
          }}
          onCreated={() => {
            adjustmentTableRef.current?.resetRowSelection();
            refetchPositions();
            refetchContracts();
          }}
        />
        <NewDataTable
          stickyHeaders
          tableClassName="h-[calc(100vh-400px)] overflow-y-scroll"
          onRowClick={handleRowClick}
          columns={allOpenPositionColumns}
          data={filteredPositions}
          rowBgColor={positionRowBgColor}
          legend={[
            { color: { preset: 'light-blue' }, label: 'Paired' },
            { color: { preset: 'light-yellow' }, label: 'Unpaired' },
          ]}
          enableSorting
          enableMultiSort
          enableMultiFilter
          enableMultiSelect
          enableFiltering
          enableGrouping
          enableColumnVisibility
          defaultCondensed
          virtualizationRowHeight={48}
          virtualizationOverscan={2}
          enableVirtualization
          enableColumnResizing
          enableColumnPinning
          enableExport
          exportFilename={() => "open_positions"}
          defaultSorting={[{ id: "date", desc: true }]}
          filterStyle="select"
          filterMode="internal"
          customToolbarButtons={customToolbarButtons}
          emptyMessage="No positions available"
          rowClickable={true}
        />
      </div>
    </>
  );
}

function PositionAdjustmentsSection({
  refetchPositions,
  refetchContracts,
}: {
  refetchPositions: () => void;
  refetchContracts: () => void;
}) {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const adjustmentsResult = useListPositionAdjustments();
  const deleteAdjustment = useDeletePositionAdjustment();
  const loadingOrErrorUi = GenerateLoadingOrError([adjustmentsResult]);

  if (loadingOrErrorUi) {
    return (
      <section className="flex flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        {loadingOrErrorUi}
      </section>
    );
  }

  const handleDelete = async (id: string) => {
    if (isDeleting) return;
    setIsDeleting(true);

    try {
      const result = await deleteAdjustment(id);
      if (!result.success) {
        toast({
          title: "Failed to delete position adjustment",
          description: result.message || "",
          variant: "destructive",
        });
        return;
      }

      refetchPositions();
      refetchContracts();
      toast({ title: "Position adjustment deleted" });
    } catch (error: unknown) {
      toast({
        title: "Failed to delete position adjustment",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: DataTableColumn<TradingAccountOpenPositionAdjustment>[] = [
    {
      id: "delete",
      header: "Delete",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          type="button"
          disabled={isDeleting}
          onClick={() => handleDelete(row.original.id)}
        >
          {isDeleting ? <Loader className="flex items-center w-4 h-4 p-0 m-0" /> : <DeleteIcon className="h-4 w-4" />}
        </Button>
      ),
      enableSorting: false,
      enableHiding: false,
      enableFiltering: false,
      disableRowClick: true,
    },
    {
      id: "adjustment_type",
      accessorKey: "adjustment_type",
      header: "Type",
    },
    {
      id: "valid_range",
      accessorFn: (adjustment: TradingAccountOpenPositionAdjustment) => adjustment.valid_from,
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
      filterParseDateFn: (value: unknown) => moment.tz(value as string, CommonDestinationTimezone),
      header: "Valid Range",
      cell: ({ row }) => {
        const validFrom = moment.tz(row.original.valid_from, CommonDestinationTimezone);
        const validTo = moment.tz(row.original.valid_to, CommonDestinationTimezone);
        const validFromLabel = validFrom.format("MM/DD/YYYY");
        const validToLabel = validTo.format("MM/DD/YYYY");
        return validFrom.isSame(validTo, "day")
          ? validFromLabel
          : `${validFromLabel} -> ${validToLabel}`;
      },
    },
    { accessorKey: "commodity", header: "Commodity" },
    { accessorKey: "option_month", header: "Reference Month" },
    { accessorKey: "strike_price", header: "Strike Price" },
    { accessorKey: "put_or_call", header: "Put/Call" },
    { accessorKey: "total_long_qty", header: "Total Long" },
    { accessorKey: "total_short_qty", header: "Total Short" },
    { accessorKey: "residual_long_qty", header: "Residual Long" },
    { accessorKey: "residual_short_qty", header: "Residual Short" },
    {
      id: "member_count",
      accessorFn: (adjustment: TradingAccountOpenPositionAdjustment) => adjustment.Members?.length ?? 0,
      header: "Positions",
    },
  ];

  return (
    <div className="flex flex-col w-full">
      <h2 className="text-lg font-bold tracking-tight mb-4">Active Position Adjustments</h2>
      <NewDataTable
        stickyHeaders
        tableClassName="h-[calc(100vh-400px)] overflow-y-scroll"
        columns={columns}
        data={adjustmentsResult.data?.data || []}
        enableSorting
        enableMultiSort
        enableFiltering
        enableMultiFilter
        enableColumnVisibility
        defaultCondensed
        enableVirtualization
        enableColumnPinning
        enableColumnResizing
        enableGrouping
        virtualizationRowHeight={48}
        virtualizationOverscan={2}
        emptyMessage="No active position adjustments"
      />
    </div>
  );
}

function ClosedContractsSection({
  customers,
  traders,
  accounts,
}: {
  customers: Customer[];
  traders: Trader[];
  accounts: TradingAccount[];
}) {
  const { toast } = useToast();
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const clear_filters = useBearStore(state => state.clear_filters);
  const contract_filters = useBearStore(
    (state) => state.contract_filters.filters,
  );
  const closedContractsRes = useListClosedContracts({ filters: contract_filters });
  const unarchiveContract = useUnArchiveContract();
  const loadingOrErrorUi = GenerateLoadingOrError([
    closedContractsRes
  ]);
  const [detailContractId, setDetailContractId] = useState<string | null>(null);

  if (loadingOrErrorUi) {
    return (
      <section className="flex flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        {loadingOrErrorUi}
      </section>
    );
  }

  const handleReOpenContract = async (id: string) => {
    if (isUnarchiving) return;
    setIsUnarchiving(true);

    const result = await unarchiveContract(id);
    if (result.success) {
      toast({
        title: "Re-Opened",
        description: "",
      });
    } else {
      toast({
        title: "Failed to Re-Open Contract",
        description: "",
      });
    }

    setIsUnarchiving(false);
  }

  const closedContractColumns: any[] = [
    {
      id: "reopen",
      header: "Re-Open",
      cell: ({ row }: any) => (
        <Button
          variant="ghost"
          type="button"
          className=""
          disabled={isUnarchiving}
          onClick={() => handleReOpenContract(row.original.id)}
        >
          {isUnarchiving ? "Re-Opening" : <StepForwardIcon className="flex items-center w-4 h-4 p-0 m-0" />}
        </Button>
      ),
      enableSorting: true,
      enableHiding: false,
      enableColumnFilter: false,
    },
    ...nonActionContractColumns,
  ];

  const AccountTypeOptions = createAccountListOptions(undefined)(accounts);
  const closedContracts = closedContractsRes.data?.data || [];

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-between w-full items-center my-2">
          <div className="flex flex-row justify-end gap-4 w-full items-center">
            <ContractFiltersModal customers={customers} accounts={accounts} traders={traders} />
            <Button
              variant="outline"
              className="mr-2"
              onClick={() => clear_filters()}
            >
              Clear Filters
            </Button>
          </div>
          <OtcContractDetailModal
            open={detailContractId !== null}
            contract_id={detailContractId}
            onClose={() => setDetailContractId(null)}
          />

        </div>
        <h2 className="text-lg font-bold tracking-tight mt-4">Closed</h2>
        <DataTable
          height="650px"
          onCellClick={(row) => setDetailContractId(row?.id ?? null)}
          columns={closedContractColumns}
          data={closedContracts?.map(
            mapToTableContract({ customers, AccountTypeOptions }),
          )}
          formId="ClosedContracts"
        />
      </div>
    </>
  );

}

function OpenContractsSection({
  isRefreshingData,
  customers,
  traders,
  openContractsPaired,
  openContractsUnpaired,
  accounts,
  refetchContracts,
  refetchPositions,
  pairs,
  openPositionsPaired,
  openPositionsUnpaired,
}: {
  isRefreshingData: boolean;
  customers: Customer[];
  traders: Trader[];
  openPositionsPaired: TradingAccountOpenPosition[];
  openPositionsUnpaired: TradingAccountOpenPosition[];
  pairs: PositionContractPair[];
  openContractsPaired: TradingContract[];
  openContractsUnpaired: TradingContract[];
  accounts: TradingAccount[];
  refetchContracts: any;
  refetchPositions: any
}) {
  const { toast } = useToast();
  const set_contracts_positions_modal_id = useBearStore(state => state.set_contracts_positions_modal_id)
  const set_new_contract = useBearStore((state) => state.set_new_contract);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadData, setDownloadData] = useState<string>("");

  const [detailContractId, setDetailContractId] = useState<string | null>(null);
  const AccountTypeOptions = createAccountListOptions(undefined)(accounts);
  const [filterType, setFilterType] = useState<"all" | "paired" | "unpaired" | "open_rolled">("all");

  // Filter contracts based on filter type
  const pairedContractIds = useMemo(() => {
    return new Set(openContractsPaired.map((c) => c.id));
  }, [openContractsPaired]);

  const filteredContracts = useMemo(() => {
    
    switch (filterType) {
      case "paired":
        return openContractsPaired;
      case "unpaired":
        return openContractsUnpaired;
      case "open_rolled": {
        return [...openContractsPaired, ...openContractsUnpaired].filter(
          (contract) =>
            contract.is_contract_open === true && contract.rolled_option_month
        );
      }
      case "all":
      default:{
        return [...openContractsPaired, ...openContractsUnpaired];
      }
    }
  }, [filterType, openContractsPaired, openContractsUnpaired]);

  const contractRowBgColor = useCallback((row: any) => {
    if (pairedContractIds.has(row.id)) {
      return { preset: 'light-blue' as const };
    }
    return { preset: 'light-yellow' as const };
  }, [pairedContractIds]);

  const onOpenNewWith = (row?: any) => {
    set_new_contract(
      !!row ? { ...row.original, id: undefined } : undefined,
    );
  };

  // Handler for row clicks to open the layered contract detail modal by id.
  const handleRowClick = (row: any, _columnId?: string) => {
    setDetailContractId(row?.id ?? null);
  };

  const allOpenContractColumns: any[] = [
    {
      id: "actions",
      header: "Actions",
      cell: ({ row, table }: any) => {
        const isCondensed = table.options.meta?.condensed;
        return (
          <div className={`flex items-center gap-2`}>
            <Button className="" variant="ghost" onClick={() => onOpenNewWith(row)}>
              <CopyIcon className="h-4 w-4" />
            </Button>
            <ArchiveContractButton id={row.original.id} refetchContracts={refetchContracts} />
            {row.original.is_manually_fully_paired
              ? <MarkContractFullyPairedContractButton show id={row.original.id} refetchContracts={refetchContracts} markStatus={false} />
              : pairedContractIds.has(row.original.id)
                ? <Button className="w-[140px]" variant="outline" disabled>Auto-Paired</Button>
                : <MarkContractFullyPairedContractButton show id={row.original.id} refetchContracts={refetchContracts} markStatus={true} />
            }
            <Button
              id="contracts_view_positions_trigger"
              variant="ghost"
              type="button"
              className=""
              onClick={() => set_contracts_positions_modal_id(row.original.id)}
            >
              <EyeIcon />
            </Button>
          </div>
        );
      },
      minSize: 320,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    },
    ...nonActionContractColumns,
  ];

  const onDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    const result = {
      success: true,
      data: await createContractsExcelBuffer({ contracts: openContracts }),
    };

    setIsDownloading(false);

    if (!result.success) {
      toast({
        title: "Failed to download",
        description: "",
      });
    } else {
      downloadBinaryData(
        result.data,
        `openContracts-${moment.tz(CommonDestinationTimezone).format("YYYYMMDD")}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      );
    }
  };

  const openContracts = [...openContractsPaired, ...openContractsUnpaired];

  // Custom toolbar buttons for filtering
  const customToolbarButtons = [
    ({ table }: { table: any }) => (
      <div className="flex gap-2">
        <Button
          variant={filterType === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("all")}
        >
          All
        </Button>
        <Button
          variant={filterType === "paired" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("paired")}
        >
          Paired
        </Button>
        <Button
          variant={filterType === "unpaired" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("unpaired")}
        >
          UnPaired
        </Button>
        <Button
          variant={filterType === "open_rolled" ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterType("open_rolled")}
        >
          <FilterIcon className="h-4 w-4 mr-2" />
          Open Rolled
        </Button>
      </div>
    ),
  ];

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-between w-full items-center my-2">
          <div className="flex flex-row justify-start w-full items-center gap-2 ">
            <Button
              variant="outline"
              disabled={isRefreshingData}
              onClick={() => { refetchPositions(); refetchContracts(); }}
            >
              {isRefreshingData ? (
                <span className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Refreshing…</span>
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <RotateCcwIcon className="w-4 h-4" />
                  Refresh Data
                </span>
              )}
            </Button>
            <DownloadContractPositionData />
            <OtcContractFormModal
              refetchContracts={refetchContracts}
              isNew={true}
              customers={customers}
              accounts={accounts}
            />
            <OtcContractDetailModal
              open={detailContractId !== null}
              contract_id={detailContractId}
              onClose={() => setDetailContractId(null)}
            />
            <ViewContractsOpenPositionsModal
              refetchContracts={refetchContracts} refetchPositions={refetchPositions}
              pairs={pairs}
              openPositionsPaired={openPositionsPaired}
              openPositionsUnpaired={openPositionsUnpaired}
            />
          </div>
        </div>
        <NewDataTable
          stickyHeaders
          tableClassName="h-[calc(100vh-400px)] overflow-y-scroll"
          onRowClick={handleRowClick}
          columns={allOpenContractColumns}
          data={filteredContracts?.map(
            mapToTableContract({ customers, AccountTypeOptions }),
          )}
          rowBgColor={contractRowBgColor}
          legend={[
            { color: { preset: 'light-blue' }, label: 'Paired' },
            { color: { preset: 'light-yellow' }, label: 'Unpaired' },
          ]}
          enableSorting
          enableMultiSort
          enableMultiFilter
          enableMultiSelect
          enableFiltering
          enableGrouping
          enableColumnVisibility
          defaultCondensed
          virtualizationRowHeight={48}
          virtualizationOverscan={2}
          enableVirtualization
          enableColumnResizing
          enableColumnPinning
          enableExport
          exportFilename={() => "open_contracts"}
          defaultSorting={[{ id: "date", desc: true }]}
          filterStyle="select"
          filterMode="internal"
          customToolbarButtons={customToolbarButtons}
          emptyMessage="No contracts available"
          rowClickable={true}
        />
      </div>
    </>
  );
}

function isPositionFormValid(formData: any) {
  const data = JSON.parse(JSON.stringify(transformToApiPosition(formData)));

  if (!CreatePositionValidation.safeParse(data).success) {
    return false;
  }

  if ((new Decimal(data.long_qty)).isZero() && (new Decimal(data.short_qty)).isZero()) {
    return false;
  }

  return true;
}


async function onSubmitPositionForm(
  {
    isEdit,
    refetchPositions,
    onCloseModal,
    createPosition,
    isSaving,
    setIsSaving,
    updateAttemptedSave,
    toast,
  }: {
    isEdit: boolean;
    refetchPositions: any;
    toast: (data: { title: string; description?: string }) => void;
    createPosition: (formData: any) => PossibleReturnType<any>;
    onCloseModal: () => void;
    isSaving: boolean;
    setIsSaving: (status: boolean) => void;
    updateAttemptedSave: (status: boolean) => void;
  },
  formData: typeof CreatePositionValidation._type,
) {
  updateAttemptedSave(true);
  // NOTE: we convert it json so it convert to how the api expects it.
  // i.e. decimals become strings
  if (!isPositionFormValid(formData)) {
    return false;
  }

  if (isSaving) return false;

  setIsSaving(true);

  const data = JSON.parse(JSON.stringify(transformToApiPosition(formData)));
  const saveResult = await (createPosition(data as any));

  updateAttemptedSave(false);
  setIsSaving(false);

  if (!saveResult.success) {
    toast({
      title: isEdit ? "Failed to update position" : "Failed to create position",
      description: "",
    });
  } else {
    onCloseModal();
    refetchPositions();
    toast({
      title: isEdit ? "Updated" : "Created",
      description: "",
    });
  }

  return true;
}

function ViewContractsOpenPositionsModal({
  refetchPositions,
  refetchContracts,
  pairs,
  openPositionsPaired,
  openPositionsUnpaired,
}: {
  refetchPositions: any
  refetchContracts: any
  openPositionsPaired: TradingAccountOpenPosition[];
  openPositionsUnpaired: TradingAccountOpenPosition[];
  pairs: PositionContractPair[];
}) {
  const { member } = useStytchMember();
  const { toast } = useToast();
  const contract_id = useBearStore(state => state.contracts_position_modal_contract_id)
  const set_contracts_positions_modal_id = useBearStore(state => state.set_contracts_positions_modal_id)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pairContract = usePairContract();
  const unpairContract = useUnPairContract();

  const setIsOpen = (status: boolean) => {
    if (!status) {
      set_contracts_positions_modal_id(undefined);
    }
  };

  const onPairClick = async (position: any, cell_id?: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const res =
      cell_id === "pair"
        ? await pairContract({
          position_id: position?.id || "",
          contract_id: contract_id || "",
        })
        : await unpairContract({
          position_id: position?.id || "",
          contract_id: contract_id || "",
        });

    if (!res.success) {
      toast({
        title: cell_id === "pair" ? "Failed to pair" : "Failed to unpair",
        description: res.message || "",
        variant: "destructive",
      });
    } else {
      refetchPositions();
      refetchContracts();
      toast({
        title: cell_id === "pair" ? "Paired" : "Unpaired",
        description: "",
      });
    }

    setIsSubmitting(false);
  };

  const openPositions = [...openPositionsPaired, ...openPositionsUnpaired].filter(
    (position: TradingAccountOpenPosition) => !position.is_position_adjustment,
  );
  const pairedPositionIds = contract_id
    ? pairs
      .filter((pair) => {
        return pair.trading_contract_id === contract_id;
      })
      .map((pair) => pair.trading_position_id)
    : [];

  const pairedPositions = openPositions.filter((pos) => {
    return pairedPositionIds.includes(pos.id);
  });

  const isOtcAdmin = doesUserHavePackages({
    requiredPackages: [PossiblePackage.OTCAdmin],
    member,
  });


  return (
    <Dialog
      onOpenChange={setIsOpen}
      open={!!contract_id}
      defaultOpen={false}
    >
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <DialogHeader>
          <DialogTitle>{"Pair Position"}</DialogTitle>
        </DialogHeader>
        <>
          {!isSubmitting && (
            <>
              {pairedPositions?.length > 0 && (
                <>
                  <h3 className="text-lg font-bold tracking-tight">
                    Currently Matched
                  </h3>
                  <DataTable
                    height="250px"
                    onCellClick={onPairClick}
                    className="max-h-[16rem] md:max-h-[24rem] lg:max-h-[30rem] grid overflow-y-auto overflow-x-auto"
                    columns={
                      isOtcAdmin
                        ? posUnpairOpenPositionColumns.map((item, index) => {
                          if (!index) return item;
                          return {
                            ...item,
                            onCellClick: false,
                          };
                        })
                        : nonActionContractColumns.map((item, index) => {
                          return {
                            ...item,
                            onCellClick: false,
                          };
                        })
                    }
                    data={pairedPositions}
                    formId="PosContractPaired"
                  />
                  <h3 className="text-lg font-bold tracking-tight my-4">
                    Un-Matched
                  </h3>
                </>
              )}
              <DataTable
                height="450px"
                onCellClick={onPairClick}
                className="max-h-[16rem] md:max-h-[24rem] lg:max-h-[30rem] grid overflow-y-auto overflow-x-auto"
                columns={posPairOpenPositionColumns.map((item, index) => {
                  if (!index) return item;
                  return {
                    ...item,
                    onCellClick: false,
                  };
                })}
                data={openPositions}
                formId="PosContractOpenPos"
              />
            </>
          )}
          {!!isSubmitting && (
            <div className="flex flex-col space-y-2 justify-center items-center my-2">
              <div className="font-semibold">Pairing</div>
            </div>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
}


function PairFormModal({
  setPairData,
  pairingData,
  customers,
  accounts,
  openContracts,
  pairs,
  refetchPositions,
  refetchContracts,
  onCloseModal,
}: {
  openContracts: TradingContract[];
  setPairData: (data: PairingData) => void;
  pairingData: PairingData;
  accounts: TradingAccount[];
  customers: Customer[];
  pairs: PositionContractPair[];
  refetchPositions: any;
  refetchContracts: any;
  onCloseModal: () => void
}) {
  const { member } = useStytchMember();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOffsetting, setIsOffsetting] = useState(false);
  const pairContract = usePairContract();
  const unpairContract = useUnPairContract();
  const [contractFilter, setContractNumberFilter] = useState('');
  const [longQtyOffset, setLongQtyOffset] = useState(0);
  const [shortQtyOffset, setShortQtyOffset] = useState(0);

  const AccountTypeOptions = createAccountListOptions(undefined)(accounts);

  const onPairClick = async (contract: TradingContract, cell_id?: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const res =
      cell_id === "pair"
        ? await pairContract({
          position_id: pairingData.position?.id || "",
          contract_id: contract.id || "",
        })
        : await unpairContract({
          position_id: pairingData.position?.id || "",
          contract_id: contract.id || "",
        });

    if (!res.success) {
      if (res.message.startsWith("Invalid pair criteria for")) {
        toast({
          title: res.message,
          description: "",
        });
      } else {
        toast({
          title: "Failed to pair",
          description: res.message || "",
          variant: "destructive",
        });
      }
    } else {
      refetchPositions();
      refetchContracts();
      toast({
        title: "Paired",
        description: "",
      });
    }

    setIsSubmitting(false);
  };

  const pairedContractIds = pairingData.position?.id
    ? pairs
      .filter((pair) => {
        return pair.trading_position_id === pairingData.position?.id;
      })
      .map((pair) => pair.trading_contract_id)
    : [];
  const pairedContracts = openContracts.filter((contract) =>
    pairedContractIds.includes(contract.id),
  );

  const isOtcAdmin = doesUserHavePackages({
    requiredPackages: [PossiblePackage.OTCAdmin],
    member,
  });

  return (
    <Dialog
      onOpenChange={(status) => {
        if (!status) { onCloseModal(); }
      }}
      open={!!pairingData.position}
      defaultOpen={false}
    >
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <h2 className="text-lg font-semibold leading-none tracking-tight">Pair Position</h2>
        {!isSubmitting && (
          <>
            {pairedContracts?.length > 0 && (
              <>
                <h3 className="text-lg font-bold tracking-tight">
                  Currently Matched
                </h3>
                <DataTable
                  height="250px"
                  onCellClick={onPairClick}
                  className="max-h-[16rem] md:max-h-[24rem] lg:max-h-[30rem] grid overflow-y-auto overflow-x-auto"
                  columns={
                    isOtcAdmin
                      ? posUnpairOpenContractColumns.map((item, index) => {
                        if (!index) return item;
                        return {
                          ...item,
                          onCellClick: false,
                        };
                      })
                      : nonActionContractColumns
                  }
                  data={pairedContracts?.map(
                    mapToTableContract({ customers, AccountTypeOptions }),
                  )}
                  formId="PairFormPaired"
                />
                <span className="flex flex-row">
                  <h3 className="text-lg font-bold tracking-tight my-4 w-full">
                    Un-Matched
                  </h3>
                  <Input
                    value={contractFilter}
                    id={"PairFormModalContractNoFilter"}
                    type="text"
                    required={false}
                    onChange={(event) => setContractNumberFilter(event.target.value || "")}
                    className={cn(
                      "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:leading-6",
                    )}
                    placeholder={"Contract No Filter"}
                  />
                </span>
              </>
            )}
            <DataTable
              height="450px"
              onCellClick={onPairClick}
              className="max-h-[16rem] md:max-h-[24rem] lg:max-h-[30rem] grid overflow-y-auto overflow-x-auto"
              columns={posPairOpenContractColumns.map((item, index) => {
                if (!index) return item;
                return {
                  ...item,
                  onCellClick: false,
                };
              })}
              data={openContracts?.filter(contract => {
                if (contractFilter !== "" && contract.contract_no) {
                  return contract.contract_no.includes(contractFilter);
                }
                if (!!pairingData.position?.put_or_call) { return !(contract.rolled_option_month || contract.rolled_basis_month); }
                return !!(contract.rolled_option_month || contract.rolled_basis_month)
              }).map(
                mapToTableContract({ customers, AccountTypeOptions }),
              ).sort((a, b) => {
                if (!a.contract_no && !b.contract_no) return 0;
                if (!a.contract_no && !!b.contract_no) return 1;
                if (!!a.contract_no && !b.contract_no) return -1;
                if (a.contract_no === b.contract_no) return 0;
                if (a.contract_no! < b.contract_no!) return -1;
                if (a.contract_no! > b.contract_no!) return 1;
                return 0;
              })}
              formId="PairFormOpen"
            />
          </>
        )}
        {!!isSubmitting && (
          <div className="flex flex-col space-y-2 justify-center items-center my-2">
            <div className="font-semibold">Pairing</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PositionFiltersModal({ accounts }: { accounts: TradingAccount[] }) {
  const isModalOpen = useBearStore(
    (state) => state.positions_filter_modal_status,
  );
  const onBaseUpdateStatus = useBearStore(
    (state) => state.set_position_filter_modal_status,
  );
  const position_filters = useBearStore(
    (state) => state.editing_position_filters.filters,
  );
  const update_position_filters_field = useBearStore(
    (state) => state.update_position_filters_field,
  );
  const persistEditingPositionFilters = useBearStore(
    (state) => state.persistPositionFilters,
  );

  const onSubmit = persistEditingPositionFilters;


  const onUpdateStatus = (status: boolean) => {
    if (!status) {
      return onSubmit();
    }

    return onBaseUpdateStatus(status);
  };

  return (
    <Dialog
      onOpenChange={onUpdateStatus}
      open={isModalOpen}
      defaultOpen={false}
    >
      <DialogTrigger asChild>
        <span className="flex flex-col justify-center items-center">
          <Label
            htmlFor="positions_filter_trigger"
            className="flex flex-row justify-between text-base items-center"
          >
            Position Filters
          </Label>
          <Button
            id="positions_filter_trigger"
            variant="ghost"
            type="button"
            className="ml-4"
            disabled={isModalOpen}
            onClick={() => onUpdateStatus(true)}
          >
            <ListFilterIcon />
          </Button>
        </span>
      </DialogTrigger>
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <DialogHeader>
          <DialogTitle>{"Position Filters"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            if (e?.preventDefault) e.preventDefault();
            return onSubmit();
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-start gap-4 content-center">
            <BearSelectMultiInput
              name="position_account_ids"
              label="Accounts"
              values={position_filters.account_ids || []}
              onValueChange={(prev: any) =>
                update_position_filters_field(
                  "account_ids",
                  prev(position_filters.account_ids || []),
                )
              }
              options={createAccountListOptions(undefined)(accounts)}
            />
            <BearSelectInput
              name="positions_commodity"
              label="Commodity"
              value={position_filters.commodity || ""}
              onValueChange={(value) =>
                update_position_filters_field(
                  "commodity",
                  findMatchOption(CommodityOptions, value)?.value,
                )
              }
              options={CommodityOptions}
            />
            <BearFloatInput
              name="positions_strike_price"
              label="Strike Price"
              value={position_filters.strike_price || ""}
              onChange={(value) =>
                update_position_filters_field("strike_price", value as any)
              }
            />
            <BearSelectInput
              name="position_filters_option_month"
              label="Reference Month"
              value={position_filters.option_month || ""}
              onValueChange={(value) =>
                update_position_filters_field(
                  "option_month",
                  findMatchOption(formulateOptionMonths(position_filters.option_month), value)?.value,
                )
              }
              options={formulateOptionMonths(position_filters.option_month)}
            />
            <div className="grid grid-cols-1 place-content-between gap-2 col-span-4">
              <div className="flex flex-row justify-between">
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    className="col-start-1"
                  >
                    Close
                  </Button>
                </DialogClose>

                <Button type="submit" size="sm" className="px-3 col-start-4">
                  {"Apply"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContractFiltersModal({
  customers,
  accounts,
  traders,
}: {
  accounts: TradingAccount[];
  traders: Trader[];
  customers: Customer[];
}) {
  const isModalOpen = useBearStore(
    (state) => state.contracts_filter_modal_status,
  );
  const onBaseUpdateStatus = useBearStore(
    (state) => state.set_contract_filter_modal_status,
  );
  const contract_filters = useBearStore(
    (state) => state.editing_contract_filters.filters,
  );
  const update_contract_filters_field = useBearStore(
    (state) => state.update_contract_filters_field,
  );
  const persistEditingContractFilters = useBearStore(
    (state) => state.persistEditingContractFilters,
  );

  const onSubmit = persistEditingContractFilters;

  const onUpdateStatus = (status: boolean) => {
    if (!status) {
      return onSubmit();
    }

    return onBaseUpdateStatus(status);
  };

  return (
    <Dialog
      onOpenChange={onUpdateStatus}
      open={isModalOpen}
      defaultOpen={false}
    >
      <DialogTrigger asChild>
        <span className="flex flex-col justify-center items-center">
          <Label
            htmlFor="contract_filter_trigger"
            className="flex flex-row justify-between text-base items-center"
          >
            Contract Filters
          </Label>
          <Button
            id="contract_filter_trigger"
            variant="ghost"
            type="button"
            className="ml-4"
            disabled={isModalOpen}
            onClick={() => onUpdateStatus(true)}
          >
            <ListFilterIcon />
          </Button>
        </span>
      </DialogTrigger>
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <DialogHeader>
          <DialogTitle>{"Contract Filters"}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            if (e?.preventDefault) e.preventDefault();
            return onSubmit();
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-start gap-4 content-center">
            <BearSelectMultiInput
              name="account_no_a_ids"
              label="Brokerage OTC"
              values={contract_filters.account_no_a_ids || []}
              onValueChange={(prev: any) =>
                update_contract_filters_field(
                  "account_no_a_ids",
                  prev(contract_filters.account_no_a_ids || []),
                )
              }
              options={createAccountListOptions(undefined)(accounts)}
            />
            <BearSelectMultiInput
              name="account_no_b_ids"
              label="Brokerage HTA"
              values={contract_filters.account_no_b_ids || []}
              onValueChange={(prev: any) =>
                update_contract_filters_field(
                  "account_no_b_ids",
                  prev(contract_filters.account_no_b_ids || []),
                )
              }
              options={createAccountListOptions(undefined)(accounts)}
            />
            <BearSelectMultiInput
              name="filters_customer_ids"
              label="Customer"
              values={contract_filters.customer_ids || []}
              onValueChange={(prev: any) =>
                update_contract_filters_field(
                  "customer_ids",
                  prev(contract_filters.customer_ids || []),
                )
              }
              options={customers.map((customer: Customer) => ({
                value: customer.id,
                label: customer.long_name,
              }))}
            />
            <BearSelectMultiInput
              name="filters_trader_ids"
              label="Trader"
              values={contract_filters.trader_ids || []}
              onValueChange={(prev: any) =>
                update_contract_filters_field(
                  "trader_ids",
                  prev(contract_filters.trader_ids || []),
                )
              }
              options={traders.map((trader: Trader) => ({
                value: trader.id,
                label: trader.name,
              }))}
            />
            <BearDatePicker
              name="filter_date"
              label="Date"
              value={contract_filters.date}
              onValueChange={(value) =>
                update_contract_filters_field("date", value)
              }
            />
            <BearDatePicker
              name="contract_filters_expiration_date"
              label="Expiration Date"
              value={contract_filters.end_date}
              onValueChange={(value) => {
                return update_contract_filters_field("end_date", value)
              }
              }
            />
            <BearSelectInput
              name="filters_contract_type"
              label="Contract Type"
              value={contract_filters.contract_type || ""}
              onValueChange={(value) =>
                update_contract_filters_field("contract_type", value)
              }
              options={ContractTypeOptions}
            />
            <BearSelectInput
              name="filters_commodity"
              label="Commodity"
              value={contract_filters.commodity || ""}
              onValueChange={(value) =>
                update_contract_filters_field(
                  "commodity",
                  findMatchOption(CommodityOptions, value)?.value,
                )
              }
              options={CommodityOptions}
            />
            <BearInput
              name="filters_trade_reference"
              label="Trade Reference"
              value={contract_filters.trade_reference || ""}
              onChange={(event) =>
                update_contract_filters_field(
                  "trade_reference",
                  event.target.value,
                )
              }
            />
            <BearInput
              name="filters_contract_no"
              label="Contract #"
              value={contract_filters.contract_no || ""}
              onChange={(event) =>
                update_contract_filters_field(
                  "contract_no",
                  event.target.value,
                )
              }
            />
            <BearSelectInput
              name="filter_option_month"
              label="OTC Opt. Month"
              value={contract_filters.option_month || ""}
              onValueChange={(value) => {
                const optionMonth = findMatchOption(formulateOptionMonths(contract_filters.option_month), value)?.value;
                return update_contract_filters_field(
                  "option_month",
                  optionMonth,
                );
              }
              }
              options={formulateOptionMonths(contract_filters.option_month)}
            />
            <BearFloatInput
              name="filter_step_one"
              label="Step 1"
              value={contract_filters.step_one || ""}
              onChange={(value) =>
                update_contract_filters_field("step_one", value)
              }
            />
            <BearFloatInput
              name="filter_step_two"
              label="Step 2"
              value={contract_filters.step_two || ""}
              onChange={(value) =>
                update_contract_filters_field("step_two", value)
              }
            />
            <BearFloatInput
              name="filter_step_three"
              label="Step 3"
              value={contract_filters.step_three || ""}
              onChange={(value) =>
                update_contract_filters_field("step_three", value)
              }
            />
            <BearFloatInput
              name="filter_double_up"
              label="Double Up"
              value={contract_filters.double_up || ""}
              onChange={(value) =>
                update_contract_filters_field("double_up", value)
              }
            />
            <div className="grid grid-cols-1 place-content-between gap-2 col-span-4">
              <div className="flex flex-row justify-between">
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    className="col-start-1"
                  >
                    Close
                  </Button>
                </DialogClose>

                <Button type="submit" size="sm" className="px-3 col-start-4">
                  {"Apply"}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DownloadFiltersForm() {
  const { toast } = useToast();
  const fields = useBearStore(state => state.downloadFilters);
  const onEditField = useBearStore(state => state.editDownloadFilterKey);
  const downloadDataLinkFunc = useDownloadOpenData();
  const [link, setLink] = useState<undefined | string>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!fields) return null;

  const onDownload = async (downloadFilters: typeof DownloadFilterValidation._type) => {
    if (isDownloading) return;
    setIsDownloading(true);

    const result = await downloadDataLinkFunc(transformGenericApiData(downloadFilters));
    setIsDownloading(false);

    if (!result.success) {
      toast({
        title: "Failed to generate",
        description: "",
      });
    } else {
      setLink(result.data.link)
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 items-start gap-4 content-center">
      <div className="grid grid-cols-2 gap-3 col-span-2 content-center">
        <BearInput
          tabIndex={0}
          name="download_filters_reference_month"
          label="Reference Month"
          value={fields.reference_month || ""}
          onChange={(event) => onEditField("reference_month", event.target.value)}
        />
        <BearDatePicker
          tabIndex={1}
          name="download_filters_contract_end_date"
          label="End Date"
          value={fields.contract_end_date as any}
          onValueChange={(date) => onEditField("contract_end_date", date)}
        />
        <BearSelectInput
          tabIndex={2}
          name="download_filters_commodity"
          label="Commodity"
          value={fields.commodity || ""}
          onValueChange={(value) =>
            onEditField(
              "commodity",
              findMatchOption(CommodityOptions, value)?.value,
            )
          }
          options={CommodityOptions}
        />
        <BearFloatInput
          tabIndex={3}
          name="download_filters_strike_price"
          label="Strike Price"
          step="5000"
          value={fields.strike_price || ""}
          onChange={(value) => onEditField("strike_price", value)}
        />
      </div>
      <div className="grid grid-cols-1 place-content-between gap-2 col-span-4">
        <div className="flex flex-row justify-between">
          {!!isDownloading && ("Downloading")}
          {!link && !isDownloading && (
            <Button variant="outline" type="button" size="sm" className="px-3 col-start-4" onClick={() => onDownload(fields)}>
              {"Generate"}
            </Button>
          )}
          {!!link && !isDownloading && (
            <a
              href={link}
              target="_blank"
              className="underline underline-offset-1 decoration-sky-500"
            >
              Download Report
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ManualPositionFormModal({
  customers,
  accounts,
  refetchPositions,
}: {
  accounts: TradingAccount[];
  customers: Customer[];
  refetchPositions: any;
}) {
  const { toast } = useToast();
  const edit_manual_position = useBearStore((state) => state.edit_manual_position);
  const set_edit_position = useBearStore((state) => state.set_edit_manual_position);
  const update_edit_position_field = useBearStore(
    (state) => state.update_edit_position_field,
  );
  const editManualPosition = useEditManualPosition();
  const [isSaving, setIsSaving] = useState(false);

  const onCloseModal = () => {
    set_edit_position(undefined);
  };

  return (
    <Dialog
      onOpenChange={(status) => {
        if (!status) { onCloseModal(); }
      }}
      open={!!edit_manual_position}
      defaultOpen={false}
    >
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <DialogHeader>
          <DialogTitle>
            Edit Position
          </DialogTitle>
        </DialogHeader>
        <PositionForm
          isEdit
          accounts={accounts}
          customers={customers}
          onEditField={update_edit_position_field}
          onSubmit={(formData) =>
            onSubmitPositionForm(
              {
                isEdit: true,
                refetchPositions,
                toast,
                createPosition: ((formData: any) => {
                  return editManualPosition(formData, edit_manual_position?.id || "");
                }) as any,
                onCloseModal,
                updateAttemptedSave: () => { },
                isSaving,
                setIsSaving,
              },
              formData,
            )
          }
        />
        <DialogFooter className="sm:justify-start"></DialogFooter>
      </DialogContent>
    </Dialog>
  );

}

function PositionFormModal({
  customers,
  accounts,
  refetchPositions,
}: {
  accounts: TradingAccount[];
  customers: Customer[];
  refetchPositions: any;
}) {
  const { toast } = useToast();
  const isModalOpen = useBearStore((state) => {
    return !!state.new_position;
  });
  const new_position = useBearStore((state) => state.new_position);
  const set_new_position = useBearStore((state) => state.set_new_position);
  const update_new_position_field = useBearStore(
    (state) => state.update_new_position_field,
  );
  const updateAttemptedSave = useBearStore(
    (state) => state.updatePositionAttemptedSave,
  );
  const createPosition = useCreatePosition();
  const [isSaving, setIsSaving] = useState(false);

  const onCloseModal = () => {
    set_new_position(undefined);
  };

  return (
    <Dialog
      onOpenChange={(status) => {
        if (!status) { onCloseModal(); }
      }}
      open={isModalOpen}
      defaultOpen={false}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          className="mr-2"
          disabled={isModalOpen}
          onClick={() => set_new_position({ short_qty: "0", long_qty: "0" })}
        >
          New Position
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-screen-2xl md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <DialogHeader>
          <DialogTitle>
            New Position
          </DialogTitle>
        </DialogHeader>
        <PositionForm
          isEdit={false}
          accounts={accounts}
          customers={customers}
          onEditField={update_new_position_field}
          onSubmit={(formData) =>
            onSubmitPositionForm(
              {
                isEdit: false,
                refetchPositions,
                toast,
                createPosition: ((formData: any) => {
                  return createPosition(formData);
                }) as any,
                onCloseModal,
                updateAttemptedSave,
                isSaving,
                setIsSaving,
              },
              formData,
            )
          }
        />
        <DialogFooter className="sm:justify-start"></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PositionForm({
  accounts,
  customers,
  onEditField,
  onSubmit,
  isEdit,
}: {
  isEdit: boolean;
  accounts: TradingAccount[];
  customers: Customer[];
  onEditField: (
    key: string,
    value?: string | number | Date | undefined | null,
  ) => void;
  onSubmit: (formData: typeof CreatePositionValidation._type) => Promise<boolean>;
}) {
  const fields = useBearStore((state) => isEdit ? state.edit_manual_position : state.new_position);
  const hasAttemptedSave = useBearStore((state) => state.hasPositionAttemptedSave);
  if (!fields) return null;

  const AccountTypeOptions = createAccountListOptions(undefined)(accounts);

  const isSubmittingDisabled = !isPositionFormValid(fields);
  const schema = CreatePositionValidation;

  return (
    <form
      onSubmit={(e) => {
        if (e?.preventDefault) e.preventDefault();
        return onSubmit(fields as any);
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 items-start gap-4 content-center">
        <div className="grid grid-cols-5 gap-3 col-span-4 content-center">
          <BearSelectInput
            tabIndex={0}
            required
            name="create_position_trading_account_id"
            label="Account"
            value={fields["trading_account_id"] || ""}
            onValueChange={(id) =>
              onEditField(
                "trading_account_id",
                findMatchOption(AccountTypeOptions, id)?.value,
              )
            }
            options={AccountTypeOptions}
          />

          <BearDatePicker
            tabIndex={1}
            name="create_position_date"
            label="Date"
            required
            value={fields.date as any}
            onValueChange={(date) => onEditField("date", date)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />

          <BearSelectInput
            tabIndex={2}
            required
            name="create_position_option_month"
            label="Reference Month"
            value={fields.option_month || ""}
            onValueChange={(value) => onEditField("option_month", findMatchOption(formulateOptionMonths(fields.option_month), value)?.value)}
            options={formulateOptionMonths(fields.option_month)}
          />

          <BearFloatInput
            tabIndex={3}
            name="create_position_long_qty"
            label="Long QTY"
            step="1"
            min="0"
            value={fields.long_qty || "0"}
            onChange={(value) => onEditField("long_qty", value || "0")}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />

          <BearFloatInput
            tabIndex={4}
            name="create_position_short_qty"
            label="Short QTY"
            step="1"
            min="0"
            value={fields.short_qty || "0"}
            onChange={(value) => onEditField("short_qty", value || "0")}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />

          <BearSelectInput
            tabIndex={5}
            name="create_positions_put_or_call"
            label="Put or Call"
            value={fields.put_or_call || ""}
            onValueChange={(value) =>
              onEditField(
                "put_or_call",
                findMatchOption(PutOrCallOptions, value)?.value,
              )
            }
            options={PutOrCallOptions}
          />

          <BearSelectInput
            tabIndex={7}
            name="create_positions_commodity"
            label="Commodity"
            value={fields.commodity || ""}
            onValueChange={(id) =>
              onEditField(
                "commodity",
                findMatchOption(CommodityOptions, id)?.value,
              )
            }
            options={CommodityOptions}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />

          <BearFloatInput
            tabIndex={8}
            name="create_position_bushel"
            label="Bushel"
            step="5000"
            value={fields.bushel || ""}
            onChange={(value) => onEditField("bushel", value)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />

          <BearFloatInput
            tabIndex={9}
            name="create_position_strike_price"
            label="Strike Price"
            step="5000"
            value={fields.strike_price || ""}
            onChange={(value) => onEditField("strike_price", value)}
            schema={schema}
            hasAttemptedSave={hasAttemptedSave}
          />




        </div>

        <div className="grid grid-cols-1 place-content-between gap-2 col-span-4">
          <div className="flex flex-row justify-between">
            <DialogClose asChild>
              <Button type="button" variant="secondary" className="col-start-1">
                Close
              </Button>
            </DialogClose>

            <Button
              type="submit"
              size="sm"
              className="px-3 col-start-4"
              disabled={isSubmittingDisabled}
            >
              {isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );

}

function DownloadContractPositionData() {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const isModalOpen = useBearStore(state => !!state.downloadFilters);
  const setDownloadFilters = useBearStore(state => state.setDownloadFilters);

  const onUpdateStatus = (status: boolean) => {
    if (!status) {
      setDownloadFilters(undefined);
    }
  }

  return (
    <Dialog
      onOpenChange={onUpdateStatus}
      open={isModalOpen}
      defaultOpen={false}
    >
      <DialogTrigger asChild>
        <span className="flex flex-col justify-center items-center">
          <Button
            id="download_data"
            variant="outline"
            type="button"
            className="mr-2"
            disabled={isModalOpen}
            onClick={() => setDownloadFilters({})}
          >
            Position Report
          </Button>
        </span>
      </DialogTrigger>
      <DialogContent className="max-w-screen-lg md:min-w-sm lg:min-w-md xl:mg-w-lg">
        <DialogHeader>
          <DialogTitle>{"Download Filters"}</DialogTitle>
        </DialogHeader>
        <>
          <DownloadFiltersForm />
        </>
      </DialogContent>
    </Dialog>
  );
}


function OpenPositions({
  refetchPositions,
  accounts,
  traders,
  areEmptyPositions,
  customers,
}: {
  refetchPositions: any;
  accounts: TradingAccount[];
  traders: Trader[];
  customers: Customer[];
  areEmptyPositions: boolean;
}) {
  return (
    <div className="flex flex-row justify-between w-full items-center">
      <OpenPositionsFilters traders={traders} accounts={accounts} customers={customers} refetchPositions={refetchPositions} />
    </div>
  );
}

function OpenPositionsFilters({
  customers,
  accounts,
  traders,
  refetchPositions,
}: {
  refetchPositions: any;
  accounts: TradingAccount[];
  traders: Trader[];
  customers: Customer[];
}) {
  const clear_filters = useBearStore(state => state.clear_filters);

  return (
    <div className="flex flex-row items-center gap-4">
      <DownloadContractPositionData />
      <PositionFormModal customers={customers} accounts={accounts} refetchPositions={refetchPositions} />
      <Button
        variant="outline"
        className="mr-2"
        onClick={() => clear_filters()}
      >
        Clear Filters
      </Button>
      <ContractFiltersModal customers={customers} accounts={accounts} traders={traders} />
    </div>
  );
}

function transformToApiPosition(item: Record<string, any>): any {
  if (!!item["commodity"]) {
    item["commodity"] = item["commodity"]?.toLocaleUpperCase();
  }

  const result = Object.entries(item).reduce(
    (acc, [key, value]) => {
      if (value === "") {
        acc[key] = undefined;
      } else if (
        typeof value === "number" ||
        posNumberKeys.includes(key)
      ) {
        try {
          acc[key] = new Decimal(value);
        } catch (error: any) {
          acc[key] = undefined;
        }
      } else if (!!value && key?.toLocaleLowerCase()?.includes("date")) {
        if (typeof value === "object") {
          acc[key] = formatDateWithoutTimezoneToDate(value);
        } else {
          acc[key] = formatDateWithoutTimezoneToDate(new Date(value));
        }
      } else if (value === null) {
        acc[key] = undefined;
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  return {
    ...result,
    contract: result.commodity,
    price: new Decimal(0),
    premium: new Decimal(0),
  }
}

function downloadCSV(filename: string, csvContent: string, clearData?: any) {
  return function downloadCSVHelper() {
    // Create a Blob
    const blob = new Blob([csvContent], { type: "text/csv" });

    // Create a link element
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute("download", filename);

    // Append the link to the body
    document.body.appendChild(link);

    // Programmatically click the link to trigger the download
    link.click();

    // Remove the link from the body
    document.body.removeChild(link);
  };
}

function MarkPositionWillExpireCheckbox({
  position,
  refetchPositions,
}: {
  position: TradingAccountOpenPosition;
  refetchPositions: () => void;
}) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const markPositionWillExpire = useMarkPositionWillExpire();

  const onCheckedChange = async (checked: boolean) => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      const result = await markPositionWillExpire({
        position_id: position.id,
        will_expire: checked,
      });

      if (!result.success) {
        toast({
          title: "Failed to update will expire status",
          description: result.message ?? "",
        });
        return;
      }

      refetchPositions();
      toast({
        title: checked ? "Marked as will expire" : "Removed will expire mark",
        description: "",
      });
    } catch (error: unknown) {
      toast({
        title: "Failed to update will expire status",
        description: error instanceof Error ? error.message : "Unexpected error",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (position.is_position_adjustment) {
    return null;
  }

  return (
    <div className="flex justify-center">
      <Checkbox
        aria-label={`Mark position ${position.id} as will expire`}
        checked={position.will_expire}
        disabled={isUpdating}
        onCheckedChange={(checked: boolean | "indeterminate") => onCheckedChange(checked === true)}
      />
    </div>
  );
}

function MarkPositionFullyPairedContractButton({ buttonClassName, id, refetchPositions, markStatus, message, buttonLabel }: { id: string; refetchPositions: any; markStatus: boolean; message: string; buttonLabel: string; buttonClassName?: string }) {
  const { toast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const markFullyPaired = useMarkPositionManualFullyPaired();

  const onArchive = async (id: string) => {
    if (isArchiving) return;
    setIsArchiving(true);

    const result = await markFullyPaired({ position_id: id, is_fully_paired: markStatus });
    setIsArchiving(false);

    if (!result.success) {
      toast({
        title: "Failed to mark",
        description: "",
      });
    } else {
      refetchPositions()
      toast({
        title: message,
        description: "",
      });
    }
  };

  return (
    <Button
      className={buttonClassName}
      variant="outline"
      onClick={() => onArchive(id)}
      disabled={isArchiving}
    >
      <span className="sr-only">{buttonLabel}</span>
      {isArchiving ? "Archiving" : <span>{buttonLabel}</span>}
    </Button>
  );
}

function MarkContractFullyPairedContractButton({ id, refetchContracts, markStatus, show }: { id: string; refetchContracts: any; markStatus: boolean; show: boolean }) {
  const { toast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const markFullyPaired = useMarkContractManualFullyPaired();

  if (!show) return null;

  const onArchive = async (id: string) => {
    if (isArchiving) return;
    setIsArchiving(true);

    const result = await markFullyPaired({ contract_id: id, is_fully_paired: markStatus });
    setIsArchiving(false);

    if (!result.success) {
      toast({
        title: "Failed to mark paired",
        description: "",
      });
    } else {
      refetchContracts()
      toast({
        title: "Marked Paired",
        description: "",
      });
    }
  };

  return (
    <Button
      className="w-[140px]"
      variant="outline"
      onClick={() => onArchive(id)}
      disabled={isArchiving}
    >
      <span className="sr-only">{markStatus ? "Mark Paired" : "Mark Un-Paired"}</span>
      {isArchiving ? "Archiving" : <span>{
        markStatus ? "Mark Paired" : "Mark Un-Paired"
      }</span>}
    </Button>
  );

}

function ArchivePositionButton({ id, refetchPositions }: { id: string; refetchPositions: any }) {
  const { toast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const archivePosition = useArchivePosition();

  const onArchive = async (id: string) => {
    if (isArchiving) return;
    setIsArchiving(true);

    const result = await archivePosition(id);
    setIsArchiving(false);

    if (!result.success) {
      toast({
        title: "Failed to archive position",
        description: "",
      });
    } else {
      refetchPositions();
      toast({
        title: "Archived",
        description: "",
      });
    }
  };

  return (
    <Button
      variant="ghost"
      className="h-8 w-8 p-0"
      onClick={() => onArchive(id)}
      disabled={isArchiving}
    >
      <span className="sr-only">Archive</span>
      {isArchiving ? "Archiving" : <ArchiveXIcon className="h-4 w-4" />}
    </Button>
  );
}

function ArchiveContractButton({ id, refetchContracts }: { id: string; refetchContracts: any }) {
  const { toast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const archiveContract = useArchiveContract();

  const onArchive = async (id: string) => {
    if (isArchiving) return;
    setIsArchiving(true);

    const result = await archiveContract(id);
    setIsArchiving(false);

    if (!result.success) {
      toast({
        title: "Failed to archive contract",
        description: result.message ?? "",
      });
    } else {
      refetchContracts();
      toast({
        title: "Archived",
        description: "",
      });
    }
  };

  return (
    <Button
      variant="ghost"
      className="h-8 w-8 p-0"
      onClick={() => onArchive(id)}
      disabled={isArchiving}
    >
      <span className="sr-only">Archive</span>
      {isArchiving ? "Archiving" : <ArchiveXIcon className="h-4 w-4" />}
    </Button>
  );
}



function mapToTableContract({
  AccountTypeOptions,
  customers,
}: {
  AccountTypeOptions: SelectOptionItem<string>[];
  customers: Customer[];
}) {
  return function mapToTableContractHelper(contract: TradingContract) {
    return {
      ...contract,
      options_account: {
        no: AccountTypeOptions.find(
          ({ value, label }: SelectOptionItem<string>) => {
            return (
              value?.toLocaleLowerCase() ===
              contract.account_no_a?.toLocaleLowerCase()
            );
          },
        )?.label,
      },
      futures_account: {
        no: AccountTypeOptions.find(
          ({ value, label }: SelectOptionItem<string>) => {
            return (
              value?.toLocaleLowerCase() ===
              contract.account_no_b?.toLocaleLowerCase()
            );
          },
        )?.label,
      },
      date_label: moment
        .tz(contract.date, CommonDestinationTimezone)
        .format("MM/DD/YYYY"),
      start_date_label: createDateLabel(contract.start_date),
      end_date_label: createDateLabel(contract.end_date),

      last_adjusted_date_label: createDateLabel(contract.last_adjusted_date),

      shipment_start_date_label: createDateLabel(contract.shipment_start_date),
      shipment_end_date_label: createDateLabel(contract.shipment_end_date),

      commodity_label:
        CommodityOptions.find((option: SelectOptionItem<string>) => {
          return (
            option.value?.toLocaleLowerCase() ===
            contract.commodity?.toLocaleLowerCase()
          );
        })?.label || contract.commodity,
      contract: {
        type: ContractTypeOptions.find(
          ({ value, label }: SelectOptionItem<string>) => {
            return (
              value?.toLocaleLowerCase() ===
              contract.contract_type?.toLocaleLowerCase()
            );
          },
        )?.label,
      },
      customer: customers.find(
        (customer) => customer.id === contract.customer_id,
      ),
    };
  };
}



function CreatePositionAdjustmentModal({
  positions,
  isOpen,
  onClose,
  onCreated,
}: {
  positions: TradingAccountOpenPosition[];
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const createAdjustment = useCreatePositionAdjustment();
  const totalLong = positions.reduce(
    (total: Decimal, position: TradingAccountOpenPosition) => total.plus(position.long_qty),
    new Decimal(0),
  );
  const totalShort = positions.reduce(
    (total: Decimal, position: TradingAccountOpenPosition) => total.plus(position.short_qty),
    new Decimal(0),
  );
  const residual = totalLong.minus(totalShort);
  const residualLabel = residual.isZero()
    ? "No residual position"
    : `${residual.isPositive() ? "Long" : "Short"} ${residual.abs().toString()}`;

  const onCreate = async () => {
    if (isCreating || positions.length < 2) return;
    setIsCreating(true);

    try {
      const result = await createAdjustment({
        position_ids: positions.map((position: TradingAccountOpenPosition) => position.id),
        valid_from: moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD"),
        valid_to: moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD"),
      });
      if (!result.success) {
        toast({
          title: "Failed to create position adjustment",
          description: result.message || "",
          variant: "destructive",
        });
        return;
      }

      onCreated();
      onClose();
      toast({ title: "Position adjustment created", description: residualLabel });
    } catch (error: unknown) {
      toast({
        title: "Failed to create position adjustment",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="sm:max-w-[625px]">
        <DialogHeader>
          <DialogTitle>Offset Selected Positions</DialogTitle>
          <DialogDescription>
            The selected positions will be hidden through the end of today in {CommonDestinationTimezone}.
            Any remaining quantity will appear as a temporary position that cannot be paired.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4 py-4">
          <div><span className="font-semibold">Total Long</span><div>{totalLong.toString()}</div></div>
          <div><span className="font-semibold">Total Short</span><div>{totalShort.toString()}</div></div>
          <div><span className="font-semibold">Result</span><div>{residualLabel}</div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isCreating}>Cancel</Button>
          <Button onClick={onCreate} disabled={isCreating || positions.length < 2}>
            {isCreating ? "Applying" : null}
            {!isCreating ? "Apply Adjustment" : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
